import { useState } from "react";
import { Link } from "react-router-dom";
import type { HelpFaq } from "@/pages/admin/helpContent";

interface HelpFaqListProps {
  faqs: HelpFaq[];
  onNavigate?: () => void;
  defaultOpen?: number | null;
}

/**
 * Reusable quick-answer list. Each question expands to reveal the answer and,
 * where relevant, a shortcut link that deep-links to the matching admin module.
 */
export default function HelpFaqList({ faqs, onNavigate, defaultOpen = 0 }: HelpFaqListProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpen);

  return (
    <ul className="space-y-2">
      {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        return (
          <li
            key={faq.q}
            className="border border-background-200 rounded-lg overflow-hidden bg-background-50"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
              aria-expanded={isOpen}
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                <i className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} text-sm`}></i>
              </span>
              <span className="text-sm font-semibold text-foreground-900 flex-1">{faq.q}</span>
            </button>

            {isOpen ? (
              <div className="px-4 pb-4">
                <div className="pl-9">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  {faq.to ? (
                    <Link
                      to={faq.to}
                      onClick={onNavigate}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {faq.toLabel || "Open"}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}